import { DateTime } from 'luxon';
import { doc, setDoc } from 'firebase/firestore';
import { FirebaseRefs } from './firebase';
import { assertNonEmpty } from './placeFunctions';
import { UserClass, userClassConverter } from './interfaces';

export type PublicUserData = {
    isLoggedIn: boolean
    status: 'logged-in' | 'logged-out' | 'unknown' | 'cached-data-available'
    username?: string | null
    firebaseUserId?: string | undefined
    displayName?: string | null
    email?: string | null
    devTools?: 'enabled' | null
    photoURL?: string | null
    // True once the users/{uid} doc has actually resolved from the database, so callers can
    // distinguish "loaded, and the field is empty" from "not loaded yet". Absent/false while the
    // public user doc is still loading.
    publicDataLoaded?: boolean
};

export type PrivateUserData = {
    countryTracking?: 'enabled' | 'disabled'
    searchProviders?: ('apple' | 'google' | 'mock')[]
    tripView?: 'plans' | 'calendar'
    // Set once the user has explicitly answered the display-name onboarding prompt (saved a name
    // or chose "Maybe later"). Stored server-side so the decision follows the account to other
    // devices and survives a reinstall, rather than living only in this device's localStorage.
    displayNamePromptDismissed?: boolean
    privateDataLoaded: boolean
};

export type UserData = PublicUserData & PrivateUserData;
export type LoggedInUserData = UserData & {
    firebaseUserId: string,
    isLoggedIn: true,
    status: 'logged-in',
};

export function getInitialsShortname(displayName: String | null) {
    let initials = '';
    let shortname = '';
    if (displayName) {
        initials = displayName?.match(/(^\S\S?|\s\S)?/g)?.map(v =>
            v.trim()).join("").match(/(^\S|\S$)?/g)?.join("").toLocaleUpperCase() ?? '';
        const firstName = displayName?.split(' ').slice(0, -1).join(' ') ?? '';
        const lastName = displayName?.split(' ').slice(-1).join(' ') ?? '';
        shortname = `${firstName} ${Array.from(lastName)[0]}`;
    }
    return {initials, shortname};
}

/**
 * Updates a UserClass with the partial values provided,
 * merging with whatever's in the database. Note UserClass
 * is separate from UserData.
 */
export async function updateUserClass(
    firebaseRefs: FirebaseRefs,
    uid: string | undefined,
    userClassUpdateFields: Partial<UserClass>) {
    const userId = assertNonEmpty(uid, 'uid');
    const userClassFields: Partial<UserClass> = {
        ...userClassUpdateFields,
        updatedAt: DateTime.now(),
    };
    const ref = doc(firebaseRefs.firestore, 'users', userId)
        .withConverter(userClassConverter);
    await setDoc(ref, userClassFields, { merge: true });
}
